import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../context/AppContext';
import { db } from '../../config/firebase';
import { doc, setDoc } from 'firebase/firestore';
import DocumentTypeSelectorModal from './DocumentTypeSelectorModal';
import PaymentModal34 from './PaymentModal34';
import UploadFormsModal34 from './UploadFormsModal34';
import RevisionModal34 from './RevisionModal34';
import RevisionPaymentModal34 from './RevisionPaymentModal34';
import RevisionRequestModal34 from './RevisionRequestModal34';

export default function Dashboard34() {
  const {
    currentUser,
    projects,
    setProjects,
    typeNames,
    documentTypes,
    getDocumentRevisionFee,
    setShowProjectModal,
    setShowPaymentModal,
    setPaymentProject,
    setShowRevisionRequestModal,
    setRevisionRequestProject,
    setRevisionData,
    logout,
    showNotification,
    approveProject,
    uploadCompletedForms
  } = useAppContext(); 

  const [showMessaging, setShowMessaging] = useState({});
  const [showDeliverables, setShowDeliverables] = useState({});
  const [replyMessages, setReplyMessages] = useState({});
  const [showDocumentTypeSelector, setShowDocumentTypeSelector] = useState(false);
  const [documentSelectorProject, setDocumentSelectorProject] = useState(null);
  const [documentSelectorAction, setDocumentSelectorAction] = useState('');

  // Filter only Stage 3 & 4 projects and remove duplicates
  const constructionProjects = projects
    .filter((project, index, self) => 
      // Remove duplicates based on project ID
      index === self.findIndex(p => p.id === project.id)
    )
    .filter(p => 
      p.stage >= 3 && (
        p.email === currentUser.email || 
        p.client === currentUser.name || 
        p.client === currentUser.email
      )
    );

  const isProjectCompleted = (project) => {
    return project.status === 'stage-3-completed' ||
           project.status === 'stage-4-completed' ||
           project.status === 'completed';
  };

  const isAdminUser = () => {
    const isAdmin = currentUser?.email === 'admin@planaidesign.com' || 
           currentUser?.email === 'bnhuang@outlook.com' ||
           currentUser?.email === 'bnhua75@gmail.com' ||
           currentUser?.email === 'ben@planaidesign.com' ||
           currentUser?.email === 'test@test.com' ||
           currentUser?.email === 'team@planaidesign.com' ||
           currentUser?.role === 'admin' ||
           currentUser?.isAdmin === true;
    
    console.log('Admin check - Current user email:', currentUser?.email);
    console.log('Admin check - Is admin:', isAdmin);
    return isAdmin;
  };

  const toggleMessaging = (projectId) => {
    setShowMessaging(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  const toggleDeliverables = (projectId) => {
    setShowDeliverables(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  const getUserStatusMessage = (project) => {
    if (project.paymentRequired && project.paymentAmount) {
      return `Payment Required: $${project.paymentAmount}`;
    }

    if (project.status === 'quote-ready') {
      return 'Quote ready for review and approval';
    }

    if (project.status === 'quote-approved') {
      return 'Quote approved - awaiting payment setup';
    }

    if (project.documentsReady) {
      return 'Documents ready for download and review';
    }

    if (project.status === 'in-progress') {
      return 'Work in progress';
    }

    if (project.status === 'application-lodged') {
      return 'Application has been lodged with the authority';
    }

    if (project.status.includes('completed')) {
      return 'Project completed';
    }

    if (project.status.includes("revision-") && project.status.includes("-requested")) {
      const revisionNumber = project.status.match(/revision-(\d+)-requested/)[1];
      return `Revision ${revisionNumber} requested - being processed`;
    }

    return null;
  };

  const canApproveProject = (project) => {
    if (!project.deliverables || project.deliverables.length === 0) return false;
    if (isProjectCompleted(project)) return false;

    if (project.status === 'quote-ready') {
      return true;
    }

    const hasPlanAIDocuments = project.deliverables.some(d =>
      d.type !== 'user-upload' && d.type !== 'quote-document'
    );

    if (hasPlanAIDocuments && !isProjectCompleted(project)) {
      return true;
    }

    return false;
  };

  const canRejectQuote = (project) => {
    return project.status === "quote-ready";
  };

  const canRequestRevision = (project) => {
    if (isProjectCompleted(project)) return false;
    if (project.status === "quote-ready") return false;

    const hasPlanAIDocuments = project.deliverables?.some(d =>
      d.type !== 'quote-document' && d.type !== 'user-upload' && d.documentType
    );

    if (hasPlanAIDocuments && !isProjectCompleted(project)) {
      return true;
    }

    return false;
  };

  const canUploadDocuments = (project) => {
    // For Stage 3 & 4, allow uploads in most states except completed
    if (project.stage >= 3) {
      return !isProjectCompleted(project) && 
             project.status !== 'quote-in-progress';
    }
    
    // Original logic for Stage 1 & 2 (if needed)
    return project.status !== 'quote-ready' &&
           project.status !== 'quote-in-progress' &&
           !isProjectCompleted(project);
  };

  const shouldShowPaymentButton = (project) => {
    // Show payment button for Stage 3/4 after quote approval
    if (project.stage >= 3) {
      // Show if quote is approved and no payments made yet
      if ((project.status === 'stage-3-approved' || project.status === 'stage-4-approved') && 
          (!project.paymentsMade || project.paymentsMade === 0)) {
        return true;
      }
      // Show if payment is explicitly required
      if (project.paymentRequired && project.paymentAmount) {
        return true;
      }
    }
    return false;
  };

  const getRevisionButtonText = (project) => {
    const recentDoc = [...(project.deliverables || [])]
      .reverse()
      .find(d => d.documentType && d.type !== 'quote-document');

    if (recentDoc && project.documentRevisionTracking) {
      const tracking = project.documentRevisionTracking[recentDoc.documentType];
      if (tracking) {
        const nextRevision = tracking.revisionsUsed + 1;
        const isAdditional = nextRevision > tracking.maxIncluded;

        if (isAdditional) {
          const fee = getDocumentRevisionFee(project.stage, recentDoc.documentType, nextRevision);
          return `Request Revision ($${fee})`;
        } else {
          return `Request Revision (Included)`;
        }
      }
    }

    return `Request Revision`;
  };

  const getDocumentRevisionSummary = (project) => {
    if (!project?.documentRevisionTracking) return null;

    const docTypes = project.stage === 3 ? documentTypes?.stage3 : documentTypes?.stage4;
    if (!docTypes) return null;
    
    const summary = [];

    Object.entries(project.documentRevisionTracking).forEach(([docType, tracking]) => {
      if (tracking?.documents?.length > 0) {
        summary.push({
          type: docType,
          label: docTypes[docType] || docType,
          uploads: tracking.documents?.length || 0,
          revisionsUsed: tracking.revisionsUsed || 0,
          maxIncluded: tracking.maxIncluded || 1
        });
      }
    });

    return summary.length > 0 ? summary : null;
  };

  const handlePaymentClick = (project) => {
    setPaymentProject(project);
    setShowPaymentModal(true);
  };

  // Add payment completion handler
  const handlePaymentCompleted = (project, paymentAmount, paymentMethod = 'card') => {
    setProjects(prev => prev.map(p => {
      if (p.id === project.id) {
        const newPaymentsMade = (p.paymentsMade || 0) + 1;
        const isInitialPayment = newPaymentsMade === 1;
        
        let confirmationMessage;
        if (isInitialPayment) {
          confirmationMessage = "Initial payment received. Thank you. Your project will commence shortly and you will be contacted by our team within 1-2 business days.";
        } else {
          confirmationMessage = `Payment ${newPaymentsMade} received: ${paymentAmount}. Thank you for your continued trust in planAIdesign.`;
        }

        const updatedProject = {
          ...p,
          paymentsMade: newPaymentsMade,
          paymentRequired: false,
          paymentAmount: null,
          paymentDescription: null,
          status: isInitialPayment ? `stage-${project.stage}-approved` : p.status,
          paymentHistory: [
            ...(p.paymentHistory || []),
            {
              amount: parseFloat(paymentAmount),
              date: new Date().toISOString(),
              type: isInitialPayment ? 'initial' : 'progress',
              method: paymentMethod
            }
          ],
          communications: [
            ...(p.communications || []),
            {
              type: "system",
              sender: "planAIdesign System",
              message: confirmationMessage,
              timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
            }
          ]
        };

        // Save to Firebase if available
        if (typeof setDoc !== 'undefined' && db) {
          const saveToFirebase = async () => {
            try {
              await setDoc(doc(db, 'projects', project.id), updatedProject);
            } catch (error) {
              console.error("Error saving to Firebase:", error);
            }
          };
          saveToFirebase();
        }

        return updatedProject;
      }
      return p;
    }));

    showNotification(
      "Payment Successful", 
      `Payment of ${paymentAmount} processed successfully.`, 
      "success"
    );
  };

  const handleRevisionRequest = (project) => {
    const uploadedDocTypes = new Set();
    project.deliverables?.forEach(d => {
      if (d.documentType && d.type !== 'quote-document' && d.type !== 'user-upload') {
        uploadedDocTypes.add(d.documentType);
      }
    });

    if (uploadedDocTypes.size === 0) {
      showNotification("No Documents", "No documents available for revision", "warning");
      return;
    }

    setDocumentSelectorProject(project);
    setDocumentSelectorAction('revise');
    setShowDocumentTypeSelector(true);
  };

  const handleDocumentTypeSelected = (selected) => {
    if (!documentSelectorProject) return;

    if (documentSelectorAction === 'revise') {
      setRevisionData({
        documentType: selected.value,
        documentTypeLabel: selected.label,
        revisionNumber: selected.nextRevision,
        fee: selected.fee,
        isAdditional: selected.isAdditional
      });

      setRevisionRequestProject(documentSelectorProject);
      setShowRevisionRequestModal(true);
    } else if (documentSelectorAction === 'approve') {
      const approvalData = {
        documentType: selected.value,
        documentTypeLabel: selected.label
      };

      approveProjectWithDocType(documentSelectorProject, approvalData);
    }

    setShowDocumentTypeSelector(false);
    setDocumentSelectorProject(null);
    setDocumentSelectorAction('');
  };

  const approveProjectWithDocType = (project, approvalData) => {
    setProjects(prev => prev.map(p => {
      if (p.id === project.id) {
        const approvedDocs = { ...(p.approvedDocuments || {}) };
        if (approvalData.documentType) {
          approvedDocs[approvalData.documentType] = {
            approvedDate: new Date().toISOString(),
            documentLabel: approvalData.documentTypeLabel
          };
        }

        let approvalMessage;
        if (approvalData.documentType === 'application-package') {
          approvalMessage = 'Thank you for approving the application package. The application will be lodged on your behalf once payment of the application fees have been received by planAIdesign.';
        } else if (approvalData.documentType === 'construction-documentation-package') {
          approvalMessage = 'Thank you for your approval. This completes Stage 4 service. Thank you for choosing planAIdesign.';
        } else {
          approvalMessage = `Thank you for approving the ${approvalData.documentTypeLabel || 'documentation'}. The other documents will be available for approval shortly.`;
        }

        return {
          ...p,
          approvedDocuments: approvedDocs,
          lastDocumentApproval: new Date().toISOString(),
          communications: [
            ...p.communications,
            {
              type: 'client',
              sender: p.client,
              message: `I approve the ${approvalData.documentTypeLabel || 'submitted documents'}.`,
              timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
            },
            {
              type: 'architect',
              sender: 'planAIdesign',
              message: approvalMessage,
              timestamp: new Date(Date.now() + 1000).toISOString().slice(0, 16).replace('T', ' ')
            }
          ]
        };
      }
      return p;
    }));

    showNotification(
      'Documents Approved',
      `${approvalData.documentTypeLabel || 'Documents'} approved successfully.`,
      'success'
    );
  };

  const handleApproveProject = (project) => {
    if (project.status === 'quote-ready') {
      // For quote approvals, add automatic message
      setProjects(prev => prev.map(p => {
        if (p.id === project.id) {
          return {
            ...p,
            status: `stage-${project.stage}-approved`,
            approvedDate: new Date().toISOString(),
            communications: [
              ...p.communications,
              {
                type: 'client',
                sender: p.client,
                message: 'I approve the quote and am ready to proceed.',
                timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
              },
              {
                type: 'architect',
                sender: 'planAIdesign',
                message: 'Thank you for approving the quote. Work will commence once signed approval documentation and deposit payment are received as outlined in the quote terms.',
                timestamp: new Date(Date.now() + 1000).toISOString().slice(0, 16).replace('T', ' ')
              }
            ]
          };
        }
        return p;
      }));
      
      showNotification('Quote Approved', 'Quote approved successfully. Next steps will be communicated shortly.', 'success');
    } else {
      // For document approvals, show document type selector
      const uploadedDocTypes = new Set();
      project.deliverables?.forEach(d => {
        if (d.documentType && d.type !== 'quote-document' && d.type !== 'user-upload') {
          uploadedDocTypes.add(d.documentType);
        }
      });

      if (uploadedDocTypes.size === 0) {
        showNotification("No Documents", "No documents available for approval", "warning");
        return;
      }

      setDocumentSelectorProject(project);
      setDocumentSelectorAction('approve');
      setShowDocumentTypeSelector(true);
    }
  };

  const handleRejectQuote = (project) => {
    setProjects(prev => prev.map(p => {
      if (p.id === project.id) {
        const message = 'Thank you for your feedback. It is unfortunate we cannot assist you with your project. All the best.';
        
        return {
          ...p,
          status: 'quote-rejected',
          rejectedDate: new Date().toISOString(),
          communications: [
            ...p.communications,
            {
              type: 'client',
              sender: project.client,
              message: 'I have decided not to proceed with the quote.',
              timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
            },
            {
              type: 'architect',
              sender: 'planAIdesign',
              message: message,
              timestamp: new Date(Date.now() + 1000).toISOString().slice(0, 16).replace('T', ' ')
            }
          ]
        };
      }
      return p;
    }));
    showNotification('Quote Rejected', 'The quote has been declined.', 'info');
  };

  const handleUploadDocuments = (project) => {
    uploadCompletedForms(project);
  };

  const sendMessage = (projectId, message) => {
    if (!message.trim()) {
      showNotification("Empty Message", "Please enter a message before sending", "warning");
      return;
    }
    showNotification("Message Sent", "Your message has been sent to planAIdesign", "success");
    setReplyMessages(prev => ({ ...prev, [projectId]: '' }));
  };

  // FIXED: Handle both string and object file formats
  const getFileName = (file) => {
    if (typeof file === 'string') {
      return file;
    }
    if (typeof file === 'object' && file?.name) {
      return file.name;
    }
    return 'Unknown File';
  };

  const downloadFile = (projectName, fileName) => {
    // Handle both string and object file formats
    const actualFileName = getFileName(fileName);
    
    const content = `This is a placeholder for ${actualFileName} from project ${projectName}.\n\nIn a real application, this would contain the actual document content.`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = actualFileName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    showNotification("Download Started", `Downloading ${actualFileName}`, "success");
  };

  const downloadAllFiles = (project, deliverable) => {
    deliverable.files.forEach((file, index) => {
      setTimeout(() => {
        downloadFile(project.name, file);
      }, index * 500);
    });

    showNotification("Downloads Started", `Downloading ${deliverable.files.length} files`, "success");
  };

  // STAGE 3 & 4 ADMIN TESTING PANEL COMPONENT - ENHANCED VERSION
  const AdminTestingPanel34 = ({ project }) => {
    const [paymentAmount, setPaymentAmount] = useState('');
    const [showPanel, setShowPanel] = useState(false);
    const [showUploadedForms, setShowUploadedForms] = useState(false);
    const [selectedDocumentType, setSelectedDocumentType] = useState('');
    const [testMode, setTestMode] = useState('basic'); // 'basic' or 'advanced'
    const [customStatus, setCustomStatus] = useState('');
    const [customMessage, setCustomMessage] = useState('');
    const [simulatePaymentAmount, setSimulatePaymentAmount] = useState('');
    const [showQuickActions, setShowQuickActions] = useState(false);

    // Enhanced payment request with multiple payment types
    const handlePaymentRequest = () => {
      if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
        showNotification("Invalid Amount", "Please enter a valid payment amount", "error");
        return;
      }

      const paymentTypes = [
        'Initial deposit payment',
        'Progress payment',
        'Final payment before lodgement',
        'Authority fees payment',
        'Consultant fees payment',
        'Additional services payment'
      ];
      
      const description = prompt(
        "Enter payment description:\n\nSuggestions:\n" + 
        paymentTypes.map((type, i) => `${i+1}. ${type}`).join('\n'),
        "Progress payment"
      );
      
      if (!description) {
        showNotification("Description Required", "Please provide a payment description", "warning");
        return;
      }

      setProjects(prev => prev.map(p => {
        if (p.id === project.id) {
          const updatedProject = {
            ...p,
            paymentRequired: true,
            paymentAmount: paymentAmount,
            paymentDescription: description,
            communications: [
              ...(p.communications || []),
              {
                type: "architect",
                sender: "planAIdesign",
                message: `${description} of ${paymentAmount} is required to proceed. Please make payment at your earliest convenience.`,
                timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
              }
            ]
          };
          
          // Save to Firebase
          const saveToFirebase = async () => {
            try {
              await setDoc(doc(db, 'projects', project.id), updatedProject);
            } catch (error) {
              console.error("Error saving to Firebase:", error);
            }
          };
          saveToFirebase();
          
          return updatedProject;
        }
        return p;
      }));
      
      setPaymentAmount('');
      showNotification("Payment Request Sent", "Payment request has been sent to client", "success");
    };

    // Simulate instant payment processing for testing
    const handleSimulatePayment = () => {
      if (!simulatePaymentAmount || parseFloat(simulatePaymentAmount) <= 0) {
        showNotification("Invalid Amount", "Please enter amount to simulate", "error");
        return;
      }

      setProjects(prev => prev.map(p => {
        if (p.id === project.id) {
          const newPaymentsMade = (p.paymentsMade || 0) + 1;
          const updatedProject = {
            ...p,
            paymentsMade: newPaymentsMade,
            paymentRequired: false,
            paymentAmount: null,
            paymentHistory: [
              ...(p.paymentHistory || []),
              {
                amount: parseFloat(simulatePaymentAmount),
                date: new Date().toISOString(),
                type: 'progress',
                method: 'test-simulation'
              }
            ],
            communications: [
              ...(p.communications || []),
              {
                type: "system",
                sender: "planAIdesign System",
                message: `Payment ${newPaymentsMade} received: ${simulatePaymentAmount} (TEST MODE)`,
                timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
              }
            ]
          };
          
          // Save to Firebase
          const saveToFirebase = async () => {
            try {
              await setDoc(doc(db, 'projects', project.id), updatedProject);
            } catch (error) {
              console.error("Error saving to Firebase:", error);
            }
          };
          saveToFirebase();
          
          return updatedProject;
        }
        return p;
      }));
      
      setSimulatePaymentAmount('');
      showNotification("Payment Simulated", `Payment of ${simulatePaymentAmount} processed`, "success");
    };

    // Enhanced document upload with workflow simulation
    const handleDocumentUpload = () => {
      // Check if this is a quote document
      const isQuote = project.status === 'quote-in-progress';
      
      let deliverable;
      let message;
      let newStatus = project.status;

      if (isQuote) {
        // Generate realistic quote based on project type
        const baseQuote = project.stage === 3 ? 
          { min: 3500, max: 8500 } : 
          { min: 4500, max: 12000 };
        
        const quoteAmount = Math.floor(Math.random() * (baseQuote.max - baseQuote.min) + baseQuote.min);
        
        deliverable = {
          type: "quote-document",
          title: `Stage ${project.stage} Quote`,
          description: `Detailed quote for ${project.stage === 3 ? 'authority approval' : 'construction'} documentation`,
          files: [`Stage_${project.stage}_Quote.pdf`, "Scope_of_Work.pdf", "Terms_and_Conditions.pdf"],
          uploadDate: new Date().toISOString().split('T')[0],
          version: 0,
          isRevision: false,
          isFinal: false,
          quoteAmount: quoteAmount
        };
        message = `Your quote is ready for review. Total fee: ${quoteAmount}. Please download and review the documentation.`;
        newStatus = "quote-ready";
      } else {
        // Document upload with specific type for Stage 3/4
        if (!selectedDocumentType) {
          showNotification("Select Document Type", "Please select a document type before uploading", "warning");
          return;
        }
        
        // Get the document type label
        const docTypes = project.stage === 3 ? documentTypes?.stage3 : documentTypes?.stage4;
        const documentTypeLabel = docTypes?.[selectedDocumentType] || selectedDocumentType;
        
        // Check if this is a revision
        const docRevisionTracking = project.documentRevisionTracking?.[selectedDocumentType];
        const isRevision = docRevisionTracking && docRevisionTracking.documents && docRevisionTracking.documents.length > 0;
        const revisionNumber = isRevision ? docRevisionTracking.documents.length : 0;
        
        deliverable = {
          type: "project-documents",
          documentType: selectedDocumentType,
          title: `${documentTypeLabel} ${isRevision ? `- Revision ${revisionNumber}` : ''}`,
          description: `${documentTypeLabel} documentation`,
          files: [`${selectedDocumentType}_documents.pdf`, `${selectedDocumentType}_drawings.pdf`],
          uploadDate: new Date().toISOString().split('T')[0],
          version: revisionNumber,
          isRevision: isRevision,
          isFinal: false
        };
        
        // Special message for Consultant Agreements (both Stage 3 and Stage 4)
        if (selectedDocumentType === 'consultant-agreements') {
          message = `Consultant Agreements uploaded for you to sign and return.`;
        } else if (selectedDocumentType === 'application-package') {
          message = `Application Package is ready for review. Once approved, we will proceed with lodgement.`;
        } else if (selectedDocumentType === 'construction-documentation-package') {
          message = `Construction Documentation Package is ready. This completes the Stage 4 documentation.`;
        } else {
          message = `${documentTypeLabel} has been uploaded for your review. Please download and review the files.`;
        }
        
        // Update document revision tracking
        setProjects(prev => prev.map(p => {
          if (p.id === project.id) {
            const tracking = p.documentRevisionTracking || {};
            if (!tracking[selectedDocumentType]) {
              tracking[selectedDocumentType] = {
                revisionsUsed: 0,
                maxIncluded: 1,
                documents: []
              };
            }
            tracking[selectedDocumentType].documents.push({
              uploadDate: new Date().toISOString(),
              version: revisionNumber
            });
            
            return {
              ...p,
              documentRevisionTracking: tracking
            };
          }
          return p;
        }));
      }

      setProjects(prev => prev.map(p => {
        if (p.id === project.id) {
          const updatedProject = {
            ...p,
            status: newStatus,
            deliverables: [...(p.deliverables || []), deliverable],
            documentsReady: true,
            communications: [
              ...(p.communications || []),
              {
                type: "architect",
                sender: "planAIdesign",
                message: message,
                timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
              }
            ]
          };
          
          // Save to Firebase
          const saveToFirebase = async () => {
            try {
              await setDoc(doc(db, 'projects', project.id), updatedProject);
              console.log('Admin document upload saved to Firebase successfully');
            } catch (error) {
              console.error("Error saving admin document upload to Firebase:", error);
            }
          };
          saveToFirebase();
          
          return updatedProject;
        }
        return p;
      }));

      showNotification("Documents Uploaded", `${isQuote ? 'Quote' : 'Documents'} uploaded successfully!`, "success");
      setSelectedDocumentType(''); // Reset selection
    };

    // Quick action workflows for common testing scenarios
    const executeQuickAction = (action) => {
      switch(action) {
        case 'complete-quote-flow':
          // Simulate quote generation → approval → payment
          if (project.status === 'quote-in-progress') {
            handleDocumentUpload(); // Upload quote
            setTimeout(() => {
              approveProject(project); // Auto-approve quote
              setTimeout(() => {
                handleSimulatePayment(); // Simulate initial payment
                showNotification("Flow Complete", "Quote → Approval → Payment flow completed", "success");
              }, 1000);
            }, 1000);
          }
          break;
          
        case 'upload-all-stage3':
          // Upload all Stage 3 documents in sequence
          const stage3Docs = ['architectural', 'structural', 'hydraulic', 'basix', 'survey'];
          stage3Docs.forEach((docType, index) => {
            setTimeout(() => {
              setSelectedDocumentType(docType);
              setTimeout(() => handleDocumentUpload(), 100);
            }, index * 1500);
          });
          showNotification("Batch Upload", "Uploading all Stage 3 documents...", "info");
          break;
          
        case 'upload-all-stage4':
          // Upload all Stage 4 documents in sequence
          const stage4Docs = ['architectural-detailed', 'structural-detailed', 'electrical', 'hydraulic-detailed'];
          stage4Docs.forEach((docType, index) => {
            setTimeout(() => {
              setSelectedDocumentType(docType);
              setTimeout(() => handleDocumentUpload(), 100);
            }, index * 1500);
          });
          showNotification("Batch Upload", "Uploading all Stage 4 documents...", "info");
          break;
          
        case 'simulate-revision-flow':
          // Simulate a complete revision workflow
          showNotification("Revision Flow", "Simulating revision request → payment → upload", "info");
          // This would trigger the revision modal programmatically
          break;
          
        case 'complete-stage':
          // Complete the current stage
          if (project.stage === 3) {
            setProjects(prev => prev.map(p => {
              if (p.id === project.id) {
                return {
                  ...p,
                  status: 'stage-3-completed',
                  communications: [
                    ...p.communications,
                    {
                      type: "system",
                      sender: "planAIdesign System",
                      message: "Stage 3 completed successfully. All documentation approved.",
                      timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
                    }
                  ]
                };
              }
              return p;
            }));
          } else if (project.stage === 4) {
            setProjects(prev => prev.map(p => {
              if (p.id === project.id) {
                return {
                  ...p,
                  status: 'stage-4-completed',
                  communications: [
                    ...p.communications,
                    {
                      type: "system",
                      sender: "planAIdesign System",
                      message: "Stage 4 completed successfully. Project finished.",
                      timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
                    }
                  ]
                };
              }
              return p;
            }));
          }
          showNotification("Stage Complete", `Stage ${project.stage} marked as completed`, "success");
          break;
          
        default:
          break;
      }
    };

    // Rest of the admin panel functions...
    const sendMessage = () => {
      const messageOptions = [
        "Thank you for your patience. We are progressing well with your documentation.",
        "We have received feedback from the council and are making necessary adjustments.",
        "The consultant reports have been received and integrated into the documentation.",
        "We require some additional information to proceed. Please see the attached questionnaire.",
        "Good news! The preliminary assessment indicates no major issues with your proposal.",
        "The structural engineer has completed their assessment and drawings.",
        "We are ready to proceed to the next stage. Please review the attached documents."
      ];
      
      const message = prompt(
        "Enter message from planAIdesign:\n\nSuggestions:\n" + 
        messageOptions.map((msg, i) => `${i+1}. ${msg}`).join('\n'),
        messageOptions[0]
      );
      
      if (!message) return;

      setProjects(prev => prev.map(p => {
        if (p.id === project.id) {
          const updatedProject = {
            ...p,
            communications: [
              ...p.communications,
              {
                type: "architect",
                sender: "planAIdesign",
                message: message,
                timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
              }
            ]
          };
          
          // Save to Firebase
          const saveToFirebase = async () => {
            try {
              await setDoc(doc(db, 'projects', project.id), updatedProject);
            } catch (error) {
              console.error("Error saving to Firebase:", error);
            }
          };
          saveToFirebase();
          
          return updatedProject;
        }
        return p;
      }));

      showNotification("Message Sent", "planAIdesign message sent", "success");
    };

    const updateStatus = () => {
      const statusOptions = project.stage === 3 ? [
        'quote-in-progress',
        'quote-ready',
        'stage-3-approved',
        'in-progress',
        'application-lodged',
        'application-approved',
        'stage-3-completed'
      ] : [
        'quote-in-progress',
        'quote-ready',
        'stage-4-approved',
        'in-progress',
        'documentation-complete',
        'stage-4-completed'
      ];
      
      const newStatus = customStatus || prompt(
        "Enter new status:\n\nOptions:\n" + 
        statusOptions.join('\n'),
        project.status
      );
      
      if (!newStatus) return;

      setProjects(prev => prev.map(p => {
        if (p.id === project.id) {
          const updatedProject = {
            ...p,
            status: newStatus
          };
          
          // Save to Firebase
          const saveToFirebase = async () => {
            try {
              await setDoc(doc(db, 'projects', project.id), updatedProject);
            } catch (error) {
              console.error("Error saving to Firebase:", error);
            }
          };
          saveToFirebase();
          
          return updatedProject;
        }
        return p;
      }));

      showNotification("Status Updated", `Status changed to: ${newStatus}`, "success");
      setCustomStatus('');
    };

    const markApplicationLodged = () => {
      setProjects(prev => prev.map(p => {
        if (p.id === project.id) {
          const updatedProject = {
            ...p,
            status: 'application-lodged',
            applicationLodgedDate: new Date().toISOString(),
            communications: [
              ...(p.communications || []),
              {
                type: "architect",
                sender: "planAIdesign",
                message: "Great news! Your application has been lodged with the relevant authority. We'll keep you updated on its progress.",
                timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
              }
            ]
          };
          
          // Save to Firebase
          const saveToFirebase = async () => {
            try {
              await setDoc(doc(db, 'projects', project.id), updatedProject);
            } catch (error) {
              console.error("Error saving to Firebase:", error);
            }
          };
          saveToFirebase();
          
          return updatedProject;
        }
        return p;
      }));

      showNotification("Application Lodged", "Application status updated", "success");
    };

    // Function to download uploaded forms (for planAIdesign side)
    const downloadUploadedForm = (file) => {
      // In a real application, this would download the actual uploaded file
      const content = `This is the uploaded form: ${file.name}\n\nUploaded on: ${file.uploadDate}\n\nIn a real application, this would be the actual uploaded document from the client.`;
      const blob = new Blob([content], { type: file.type || 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      showNotification("Download Started", `Downloading client upload: ${file.name}`, "success");
    };

    // Show controls based on project status and stage
    const shouldShowPaymentRequest = () => {
      // Always show payment request for Stage 3/4 after quote approval
      if (project.stage >= 3 && (
        project.status === 'stage-3-approved' || 
        project.status === 'stage-4-approved' || 
        project.status === 'in-progress' ||
        project.status === 'application-lodged'
      )) {
        return true;
      }
      return true;
    };

    const shouldShowDocumentUpload = () => {
      return true;
    };

    const shouldShowApplicationLodged = () => {
      return project.stage === 3 && (
        project.status === 'stage-3-approved' || 
        project.status === 'in-progress'
      );
    };

    const hasUploadedForms = () => {
      return project.uploadedForms && project.uploadedForms.length > 0;
    };

    // Get document types for current stage
    const getDocumentTypesForStage = () => {
      if (project.stage === 3) return documentTypes?.stage3 || {};
      if (project.stage === 4) return documentTypes?.stage4 || {};
      return {};
    };

    // Get revision status for each document type
    const getDocumentRevisionStatus = () => {
      if (!project.documentRevisionTracking) return null;
      
      const docTypes = getDocumentTypesForStage();
      const status = {};
      
      Object.keys(docTypes).forEach(docType => {
        const tracking = project.documentRevisionTracking[docType];
        if (tracking) {
          status[docType] = {
            label: docTypes[docType],
            uploads: tracking.documents.length,
            revisionsUsed: tracking.revisionsUsed,
            maxIncluded: tracking.maxIncluded
          };
        }
      });
      
      return status;
    };

    const revisionStatus = getDocumentRevisionStatus();

    return (
      <div className="mt-4 p-4 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
        <div className="flex justify-between items-center mb-3">
          <h4 className="font-bold text-sm text-yellow-800">
            Testing Admin Panel (Remove in Production)
          </h4>
          <button
            onClick={() => setShowPanel(!showPanel)}
            className="text-xs text-yellow-600 hover:text-yellow-800"
          >
            {showPanel ? 'Hide' : 'Show'} Controls
          </button>
        </div>

        {showPanel && (
          <div className="space-y-3">
            {/* Test Mode Toggle */}
            <div className="bg-white p-3 rounded border border-yellow-300">
              <div className="flex justify-between items-center">
                <h5 className="font-semibold text-xs">Testing Mode</h5>
                <div className="flex gap-2">
                  <button
                    onClick={() => setTestMode('basic')}
                    className={`px-2 py-1 text-xs rounded ${testMode === 'basic' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                  >
                    Basic
                  </button>
                  <button
                    onClick={() => setTestMode('advanced')}
                    className={`px-2 py-1 text-xs rounded ${testMode === 'advanced' ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}
                  >
                    Advanced
                  </button>
                </div>
              </div>
            </div>

            {/* Quick Actions - Advanced Mode */}
            {testMode === 'advanced' && (
              <div className="bg-white p-3 rounded border border-yellow-300">
                <div className="flex justify-between items-center mb-2">
                  <h5 className="font-semibold text-xs text-indigo-700">Quick Actions</h5>
                  <button
                    onClick={() => setShowQuickActions(!showQuickActions)}
                    className="text-xs text-indigo-600 hover:text-indigo-800"
                  >
                    {showQuickActions ? 'Hide' : 'Show'}
                  </button>
                </div>
                
                {showQuickActions && (
                  <div className="space-y-2">
                    {project.status === 'quote-in-progress' && (
                      <button
                        onClick={() => executeQuickAction('complete-quote-flow')}
                        className="w-full bg-indigo-600 text-white px-2 py-1 rounded text-xs hover:bg-indigo-700"
                      >
                        Complete Quote → Approval → Payment Flow
                      </button>
                    )}
                    
                    {project.stage === 3 && project.status !== 'quote-in-progress' && (
                      <button
                        onClick={() => executeQuickAction('upload-all-stage3')}
                        className="w-full bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700"
                      >
                        Upload All Stage 3 Documents
                      </button>
                    )}
                    
                    {project.stage === 4 && project.status !== 'quote-in-progress' && (
                      <button
                        onClick={() => executeQuickAction('upload-all-stage4')}
                        className="w-full bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700"
                      >
                        Upload All Stage 4 Documents
                      </button>
                    )}
                    
                    <button
                      onClick={() => executeQuickAction('complete-stage')}
                      className="w-full bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700"
                    >
                      Complete Current Stage
                    </button>
                    
                    <p className="text-xs text-gray-600 mt-1 italic">
                      Quick actions simulate complete workflows instantly
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Payment Simulation - Enhanced */}
            {testMode === 'advanced' && (
              <div className="bg-white p-3 rounded border border-yellow-300">
                <h5 className="font-semibold text-xs mb-2 text-green-700">Instant Payment Simulation</h5>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={simulatePaymentAmount}
                    onChange={(e) => setSimulatePaymentAmount(e.target.value)}
                    placeholder="Amount"
                    className="flex-1 p-2 border rounded text-xs"
                    step="100"
                  />
                  <button
                    onClick={handleSimulatePayment}
                    className="bg-green-600 text-white px-3 py-2 rounded text-xs hover:bg-green-700"
                  >
                    Simulate Payment
                  </button>
                </div>
                <p className="text-xs text-gray-600 mt-1">Instantly process payment without client action</p>
              </div>
            )}

            {/* Document Revision Tracking for Stage 3/4 */}
            {project.stage >= 3 && revisionStatus && Object.keys(revisionStatus).length > 0 && (
              <div className="bg-white p-3 rounded border border-yellow-300">
                <h5 className="font-semibold text-xs mb-2 text-purple-700">Document Revision Tracking</h5>
                <div className="space-y-1 text-xs">
                  {Object.entries(revisionStatus).map(([docType, info]) => (
                    <div key={docType} className="flex justify-between items-center bg-purple-50 p-1 rounded">
                      <span className="font-medium">{info.label}:</span>
                      <span>
                        {info.uploads} upload(s) | 
                        {info.revisionsUsed}/{info.maxIncluded} revisions used
                        {/* Show if consultants have unlimited additional revisions */}
                        {docType !== 'architectural' && docType !== 'architectural-detailed' && info.revisionsUsed > info.maxIncluded && (
                          <span className="text-green-600 ml-1">(no fee)</span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-600 mt-2 italic">
                  Consultant documents have unlimited revisions after the first included revision
                </p>
              </div>
            )}

            {/* Client Uploaded Forms Section - ADMIN SIDE */}
            {hasUploadedForms() && (
              <div className="bg-white p-3 rounded border border-yellow-300">
                <div className="flex justify-between items-center mb-2">
                  <h5 className="font-semibold text-xs text-teal-700">Client Uploaded Forms</h5>
                  <button
                    onClick={() => setShowUploadedForms(!showUploadedForms)}
                    className="text-xs text-teal-600 hover:text-teal-800"
                  >
                    {showUploadedForms ? 'Hide' : 'Show'} ({project.uploadedForms.length})
                  </button>
                </div>
                
                {showUploadedForms && (
                  <div className="space-y-2 mt-2">
                    {project.uploadedForms.map((file, idx) => (
                      <div key={idx} className="bg-teal-50 p-2 rounded flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-xs font-medium">{file.name}</p>
                          <p className="text-xs text-gray-600">
                            {(file.size / 1024 / 1024).toFixed(2)} MB • {new Date(file.uploadDate).toLocaleDateString()}
                          </p>
                        </div>
                        <button
                          onClick={() => downloadUploadedForm(file)}
                          className="bg-teal-600 text-white px-2 py-1 rounded text-xs hover:bg-teal-700"
                        >
                          Download
                        </button>
                      </div>
                    ))}
                    <p className="text-xs text-gray-600 mt-1 italic">
                      These are forms uploaded by the client
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Payment Request */}
            {shouldShowPaymentRequest() && (
              <div className="bg-white p-3 rounded border border-yellow-300">
                <h5 className="font-semibold text-xs mb-2 text-green-700">Request Payment</h5>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    placeholder="Amount"
                    className="flex-1 p-2 border rounded text-xs"
                    step="0.01"
                  />
                  <button
                    onClick={handlePaymentRequest}
                    className="bg-green-600 text-white px-3 py-2 rounded text-xs hover:bg-green-700"
                  >
                    Send Request
                  </button>
                </div>
                <p className="text-xs text-gray-600 mt-1">Flexible payment requests for progress billing</p>
              </div>
            )}

            {/* Document Upload with Type Selection for Stage 3/4 */}
            {shouldShowDocumentUpload() && (
              <div className="bg-white p-3 rounded border border-yellow-300">
                <h5 className="font-semibold text-xs mb-2 text-blue-700">Upload Documents</h5>
                
                {/* Document Type Selection for Stage 3/4 */}
                {project.stage >= 3 && project.status !== 'quote-in-progress' && (
                  <select
                    value={selectedDocumentType}
                    onChange={(e) => setSelectedDocumentType(e.target.value)}
                    className="w-full p-2 border rounded text-xs mb-2"
                  >
                    <option value="">Select Document Type...</option>
                    {Object.entries(getDocumentTypesForStage()).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                        {project.documentRevisionTracking?.[key] && 
                          ` (${project.documentRevisionTracking[key].documents.length} uploads)`
                        }
                      </option>
                    ))}
                  </select>
                )}
                
                <button
                  onClick={handleDocumentUpload}
                  className="w-full bg-blue-600 text-white px-3 py-2 rounded text-xs hover:bg-blue-700"
                  disabled={project.stage >= 3 && project.status !== 'quote-in-progress' && !selectedDocumentType}
                >
                  {project.status === 'quote-in-progress' ? 
                    `Upload Stage ${project.stage} Quote` : 'Simulate Document Upload'}
                </button>
                
                {project.stage >= 3 && project.status === 'quote-in-progress' && (
                  <p className="text-xs text-blue-600 mt-1">This will upload a quote document</p>
                )}
                {project.stage >= 3 && (
                  project.status === 'stage-3-approved' || 
                  project.status === 'stage-4-approved' ||
                  project.status === 'in-progress' ||
                  project.status === 'application-lodged'
                ) && (
                  <p className="text-xs text-green-600 mt-1">
                    Select document type to upload specific documentation
                  </p>
                )}
              </div>
            )}

            {/* Mark Application Lodged - Stage 3 only */}
            {shouldShowApplicationLodged() && (
              <div className="bg-white p-3 rounded border border-yellow-300">
                <h5 className="font-semibold text-xs mb-2 text-indigo-700">Application Status</h5>
                <button
                  onClick={markApplicationLodged}
                  className="w-full bg-indigo-600 text-white px-3 py-2 rounded text-xs hover:bg-indigo-700"
                >
                  Mark Application as Lodged
                </button>
              </div>
            )}

            {/* Send Message */}
            <div className="bg-white p-3 rounded border border-yellow-300">
              <h5 className="font-semibold text-xs mb-2 text-purple-700">Send Message</h5>
              <button
                onClick={sendMessage}
                className="w-full bg-purple-600 text-white px-3 py-2 rounded text-xs hover:bg-purple-700"
              >
                Send planAIdesign Message
              </button>
            </div>

            {/* Update Status */}
            <div className="bg-white p-3 rounded border border-yellow-300">
              <h5 className="font-semibold text-xs mb-2 text-orange-700">Update Status</h5>
              <button
                onClick={updateStatus}
                className="w-full bg-orange-600 text-white px-3 py-2 rounded text-xs hover:bg-orange-700"
              >
                Change Project Status
              </button>
            </div>

            {/* Custom Status Update - Advanced Mode */}
            {testMode === 'advanced' && (
              <div className="bg-white p-3 rounded border border-yellow-300">
                <h5 className="font-semibold text-xs mb-2 text-orange-700">Custom Status Update</h5>
                <input
                  type="text"
                  value={customStatus}
                  onChange={(e) => setCustomStatus(e.target.value)}
                  placeholder="Enter custom status..."
                  className="w-full p-2 border rounded text-xs mb-2"
                />
                <button
                  onClick={updateStatus}
                  className="w-full bg-orange-600 text-white px-3 py-2 rounded text-xs hover:bg-orange-700"
                >
                  Set Custom Status
                </button>
              </div>
            )}

            {/* Custom Message - Advanced Mode */}
            {testMode === 'advanced' && (
              <div className="bg-white p-3 rounded border border-yellow-300">
                <h5 className="font-semibold text-xs mb-2 text-purple-700">Custom Message</h5>
                <textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Enter custom message..."
                  className="w-full p-2 border rounded text-xs mb-2"
                  rows="3"
                />
                <button
                  onClick={() => {
                    if (customMessage) {
                      setProjects(prev => prev.map(p => {
                        if (p.id === project.id) {
                          const updatedProject = {
                            ...p,
                            communications: [
                              ...(p.communications || []),
                              {
                                type: "architect",
                                sender: "planAIdesign",
                                message: customMessage,
                                timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
                              }
                            ]
                          };
                          
                          // Save to Firebase
                          const saveToFirebase = async () => {
                            try {
                              await setDoc(doc(db, 'projects', project.id), updatedProject);
                            } catch (error) {
                              console.error("Error saving to Firebase:", error);
                            }
                          };
                          saveToFirebase();
                          
                          return updatedProject;
                        }
                        return p;
                      }));
                      setCustomMessage('');
                      showNotification("Message Sent", "Custom message sent", "success");
                    }
                  }}
                  className="w-full bg-purple-600 text-white px-3 py-2 rounded text-xs hover:bg-purple-700"
                >
                  Send Custom Message
                </button>
              </div>
            )}

            {/* Keyboard Shortcuts Reminder - Enhanced */}
            <div className="bg-yellow-100 p-2 rounded text-xs">
              <strong>Keyboard Shortcuts:</strong>
              <ul className="mt-1 text-gray-700">
                <li>• Ctrl+Shift+Q: Send Quote (Stage 3/4)</li>
                <li>• Ctrl+Shift+D: Upload Documents</li>
                <li>• Ctrl+Shift+M: Send Message</li>
                <li>• Ctrl+Shift+U: Update Status</li>
                <li>• Ctrl+Shift+P: Request Payment</li>
                <li>• Ctrl+Shift+L: Lodge Application (Stage 3)</li>
              </ul>
              {testMode === 'advanced' && (
                <p className="text-gray-600 mt-1 italic">
                  Advanced mode enables workflow automation and batch operations
                </p>
              )}
            </div>

            {/* Testing Info */}
            <div className="bg-red-50 p-2 rounded text-xs border border-red-200">
              <strong className="text-red-700">Testing Panel Active</strong>
              <p className="text-gray-700 mt-1">
                Project ID: {project.id}<br/>
                Stage: {project.stage} | Status: {project.status}<br/>
                Payments Made: {project.paymentsMade || 0}
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-gradient-to-r from-slate-900 to-blue-700 text-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <h1 className="text-2xl font-bold">
              <span className="text-lg">plan</span>
              <span className="text-yellow-400 text-2xl">AI</span>
              <span className="text-lg">design</span>
              <span className="ml-2 text-sm">- Stage 3 & 4</span>
            </h1>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-100">Welcome, {currentUser.name}</span>
              <button onClick={logout} className="text-sm text-gray-200 hover:text-white">
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
            <div className="text-3xl font-bold text-gray-900">{constructionProjects.length}</div>
            <div className="text-sm text-gray-600">Total Projects (Stage 3 & 4)</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
            <div className="text-3xl font-bold text-blue-600">
              {constructionProjects.filter(p => !isProjectCompleted(p)).length}
            </div>
            <div className="text-sm text-gray-600">Active Projects</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
            <div className="text-3xl font-bold text-green-600">
              {constructionProjects.filter(p => isProjectCompleted(p)).length}
            </div>
            <div className="text-sm text-gray-600">Completed</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
            <h2 className="text-xl font-semibold">Your Stage 3 & 4 Projects</h2>
            <button
              onClick={() => setShowProjectModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors shadow-md"
            >
              + New Project
            </button>
          </div>
          <div className="p-6">
            {constructionProjects.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 text-5xl mb-4">🏗️</div>
                <p className="text-gray-600 mb-4">No Stage 3 or 4 projects yet</p>
                <button
                  onClick={() => setShowProjectModal(true)}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Create Your First Project
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {constructionProjects.map(project => {
                  const revisionSummary = getDocumentRevisionSummary(project);

                  return (
                    <div key={project.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="text-lg font-semibold">{project.name}</h3>
                          <p className="text-sm text-gray-600">
                            {typeNames[project.type]} • Stage {project.stage} • {project.cost}
                          </p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          project.status.includes('completed') ? 'bg-green-100 text-green-800' :
                          project.status === 'stage-3-approved' ? 'bg-green-100 text-green-800' :
                          project.status === 'stage-4-approved' ? 'bg-green-100 text-green-800' :
                          project.status === 'application-lodged' ? 'bg-indigo-100 text-indigo-800' :
                          project.status === 'quote-rejected' ? 'bg-red-100 text-red-800' :
                          project.status.includes('progress') ? 'bg-blue-100 text-blue-800' :
                          project.status.includes('ready') ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {project.status === 'stage-3-approved' ? 'stage 3 quote approved' :
                           project.status === 'stage-4-approved' ? 'stage 4 quote approved' :
                           project.status.replace(/-/g, ' ')}
                        </span>
                      </div>

                      {getUserStatusMessage(project) && (
                        <div className="bg-blue-50 p-3 rounded mb-3 text-sm">
                          {getUserStatusMessage(project)}
                        </div>
                      )}

                      <div className="flex gap-2 flex-wrap">
                        {shouldShowPaymentButton(project) && (
                          <button
                            onClick={() => handlePaymentClick(project)}
                            className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700"
                          >
                            {project.paymentRequired && project.paymentAmount ? 'Make Payment' : 'Make Initial Payment'}
                          </button>
                        )}

                        {canApproveProject(project) && (
                          <button
                            onClick={() => handleApproveProject(project)}
                            className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700"
                          >
                            Approve {project.status === 'quote-ready' ? 'Quote' : 'Documents'}
                          </button>
                        )}

                        {canRejectQuote(project) && (
                          <button
                            onClick={() => handleRejectQuote(project)}
                            className="bg-red-600 text-white px-4 py-2 rounded text-sm hover:bg-red-700"
                          >
                            Reject Quote
                          </button>
                        )}

                        {canRequestRevision(project) && (
                          <button
                            onClick={() => handleRevisionRequest(project)}
                            className="bg-orange-600 text-white px-4 py-2 rounded text-sm hover:bg-orange-700"
                          >
                            {getRevisionButtonText(project)}
                          </button>
                        )}

                        {canUploadDocuments(project) && (
                          <button
                            onClick={() => handleUploadDocuments(project)}
                            className="bg-indigo-600 text-white px-4 py-2 rounded text-sm hover:bg-indigo-700"
                          >
                            Upload Documents
                          </button>
                        )}

                        {project.deliverables && project.deliverables.length > 0 && (
                          <button
                            onClick={() => toggleDeliverables(project.id)}
                            className="bg-purple-600 text-white px-4 py-2 rounded text-sm hover:bg-purple-700"
                          >
                            Documents ({project.deliverables.length})
                          </button>
                        )}

                        {project.communications && project.communications.length > 0 && (
                          <button
                            onClick={() => toggleMessaging(project.id)}
                            className="bg-gray-600 text-white px-4 py-2 rounded text-sm hover:bg-gray-700"
                          >
                            Messages ({project.communications.length})
                          </button>
                        )}
                      </div>

                      {showDeliverables[project.id] && project.deliverables && (
                        <div className="mt-4 border-t pt-4">
                          <h4 className="font-semibold text-sm mb-3">Project Documents</h4>
                          <div className="space-y-3">
                            {project.deliverables.map((deliverable, idx) => (
                              <div key={idx} className="bg-gray-50 p-3 rounded">
                                <div className="flex justify-between items-start mb-2">
                                  <div>
                                    <h5 className="font-semibold text-sm">{deliverable.title}</h5>
                                    <p className="text-xs text-gray-600">{deliverable.description}</p>
                                    <p className="text-xs text-gray-500 mt-1">
                                      Uploaded: {deliverable.uploadDate}
                                      {deliverable.isRevision && ' (Revision)'}
                                      {deliverable.documentType && (
                                        <span className="ml-2 text-purple-600">
                                          Type: {documentTypes[`stage${project.stage}`]?.[deliverable.documentType] || deliverable.documentType}
                                        </span>
                                      )}
                                    </p>
                                  </div>
                                  <button
                                    onClick={() => downloadAllFiles(project, deliverable)}
                                    className="bg-blue-600 text-white px-3 py-1 rounded text-xs hover:bg-blue-700"
                                  >
                                    Download All
                                  </button>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                                  {deliverable.files.map((file, fileIdx) => (
                                    <div key={fileIdx} className="flex items-center justify-between bg-white p-2 rounded border">
                                      <span className="text-xs truncate flex-1">{getFileName(file)}</span>
                                      <button
                                        onClick={() => downloadFile(project.name, file)}
                                        className="text-blue-600 hover:text-blue-800 ml-2"
                                        title="Download file"
                                      >
                                        Download
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {showMessaging[project.id] && project.communications && project.communications.length > 0 && (
                        <div className="mt-4 border-t pt-4">
                          <div className="max-h-48 overflow-y-auto space-y-2 mb-3">
                            {project.communications.map((comm, idx) => (
                              <div
                                key={idx}
                                className={`p-3 rounded text-sm ${
                                  comm.type === 'client'
                                    ? 'bg-blue-100 ml-8'
                                    : 'bg-gray-100 mr-8'
                                }`}
                              >
                                <div className="flex justify-between mb-1">
                                  <span className="font-semibold">
                                    {comm && comm.sender ? String(comm.sender) : 'Unknown'}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {comm && comm.timestamp ? String(comm.timestamp) : ''}
                                  </span>
                                </div>
                                <p>{comm && comm.message ? String(comm.message) : ''}</p>
                              </div>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="Type your message..."
                              value={replyMessages[project.id] || ''}
                              onChange={(e) => setReplyMessages(prev => ({
                                ...prev,
                                [project.id]: e.target.value
                              }))}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  sendMessage(project.id, replyMessages[project.id] || '');
                                }
                              }}
                              className="flex-1 p-2 border rounded text-sm focus:border-blue-500 focus:outline-none"
                            />
                            <button
                              onClick={() => sendMessage(project.id, replyMessages[project.id] || '')}
                              className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700"
                            >
                              Send
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Admin Testing Panel for Stage 3 & 4 */}
                      {isAdminUser() && <AdminTestingPanel34 project={project} />}
                      
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>

      <DocumentTypeSelectorModal
        show={showDocumentTypeSelector}
        onClose={() => {
          setShowDocumentTypeSelector(false);
          setDocumentSelectorProject(null);
          setDocumentSelectorAction('');
        }}
        project={documentSelectorProject}
        onSelect={handleDocumentTypeSelected}
        action={documentSelectorAction}
      />
      
      <PaymentModal34 />
      <UploadFormsModal34 />
      <RevisionModal34 />
      <RevisionPaymentModal34 />
      <RevisionRequestModal34 />
    </div>
  );
}